// Main store

import Vue from "vue";
import Vuex from "vuex";
import ndvrPlayer from "@/store/modules/ndvrPlayer";
import timeline from "@/store/modules/timeline";
import thumbnails from "@/store/modules/thumbnails";
import socials from "@/store/modules/socials";
import admin from "@/store/modules/admin";
import timezone from "@/store/modules/timezone";
import modals from "@/store/modules/modals";

import { setFontsPath } from "@/api/fonts.js";

import { CHANNEL_TYPE, DEFAULT_CHANNEL_TYPE } from "@/constants.js";

import defaultAudioPoster from "@/assets/images/audio_channel_default.jpg";
import defaultVideoPoster from "@/assets/images/loading_stream.jpg";

Vue.use(Vuex);

export default new Vuex.Store({
  /** this is all states we using for our applications */
  state: {
    /** default is  */
    services: {
      isStudioMode: false,
      isMediaManager: false,
      /** current channel type (audio/video) */
      channelType: DEFAULT_CHANNEL_TYPE,
      /** audio poster */
      channelPoster: null,
      /** Current channel ID */
      mangomoloChannelId: "",
      /** Wowza server security token */
      stream_token: undefined,
      /** Wowza base URL */
      host: undefined,
      /** Current Wowza live application name */
      app: undefined,
      /** Current Wowza VOD application name */
      vod: undefined,
      /** Current stream name (for URL) */
      stream: undefined,
      /** Thumbnails storage base URL */
      thumbs: undefined,
      sharingChannelIdMangomolo: undefined
    },
    userData: {
      /** User ID */
      user_id: undefined,
      username: undefined,
      /** Mangomolo user ID */
      mango_user_id: undefined,
      features: undefined,
      /** List of available channels */
      channels: [],
      /** Current channel ID */
      currentChannel: null,
      /** User data loaded flag */
      initialized: false,
      /** Channel timezone */
      defaultTimezone: "UTC"
    },
    studioEdit: {
      studioMode: ""
    }
  },
  getters: {
    hasThumbnails(state) {
      return state.services.channelType == CHANNEL_TYPE.VIDEO;
    },
    hasSudioMode(state) {
      return state.studioEdit.studioMode;
    },
    channelType(state) {
      return state.services.channelType;
    }
  },
  mutations: {
    updateStudioEdit(state, mode) {
      state.studioEdit.studioMode = mode;
    },
    
    updateChannelTypeForMM(state, chanelType) {
      state.services.channelType = chanelType;
      /** channel poster */
      if (state.services.channelType == CHANNEL_TYPE.AUDIO) {
        state.services.channelPoster = defaultAudioPoster;
      } else {
        state.services.channelPoster = defaultVideoPoster;
      }
    },
    /** Set user data */
    setMediaManager(state, mode) {
      state.services.isMediaManager = mode;
    },
    setStudioMode(state, mode) {
      state.services.isStudioMode = mode;
    },
    initUserData(state, userData) {
      state.userData.user_id = userData.user_id;
      state.userData.username = userData.username;
      state.userData.mango_user_id = userData.mango_user_id;
      state.userData.features = userData.features;
      state.userData.channels = userData.channels;
      state.userData.defaultTimezone = userData.client_timezone;
      state.userData.initialized = true;
    },
    /** Set channel parameters and some URL params */
    setChannelState(state, channel) {
      state.services.stream_token = channel.stream_token;
      state.services.host = channel.stream_host;
      state.services.stream = channel.default_stream;
      state.services.thumbs = channel.ndvr_nginx;
      state.services.httpStorage = channel.ndvr_http_nginx;
      state.userData.currentChannel = channel.id;
      state.services.mangomoloChannelId = channel.channel_id;

      /** set channel type */
      state.services.channelType = channel.only_audio
        ? CHANNEL_TYPE.AUDIO
        : CHANNEL_TYPE.VIDEO;

      /** channel poster */
      if (state.services.channelType == CHANNEL_TYPE.AUDIO) {
        state.services.channelPoster =
          channel.channel_cover ?? defaultAudioPoster;
      } else {
        state.services.channelPoster =
          channel.channel_cover ?? defaultVideoPoster;
      }
      // Set fonts URL
      setFontsPath(channel.ndvr_nginx + "/fonts");
    },
    setThumbs(state, thumbs) {
      state.services.thumbs = thumbs;
    },
    setHost(state, host) {
      state.services.host = host;
    },
    setSharingChannel(state, id) {
      state.services.sharingChannelIdMangomolo = id;
    },
    setVod(state, vod) {
      state.services.vod = vod;
    }
  },
  actions: {
    setMediaManagerMode({ state, commit }, { data }) {
      commit("setMediaManager", data);
    },
    setUpdateShannelType({ state, commit }, data) {
      commit("updateChannelTypeForMM", data);
    },
    setStudioVideoMode({ state, commit }, { data }) {
      commit("setStudioMode", data);
    },
    /** Set user info and init channel */
    initializeUserData({ state, commit, dispatch }, { userData, channelId }) {
      userData.channels.forEach(
        channel => (channel.id = channel.stream_id ?? channel.id)
      );
      commit("initUserData", userData);
      if (!channelId) {
        dispatch("initStartChannel");
      } else {
        dispatch("setChannel", channelId);
      }

      /** load thumbnails */
      if ((state.services.channelType == CHANNEL_TYPE.VIDEO) && channelId) {
        dispatch("thumbnails/loadThumbnails", {}, { root: true });
      }
    },
    initStartChannel({ state, dispatch }) {
      if (state.services.isMediaManager) {
        dispatch("setChannel", -1);
      }
      // const defaultChannel = state.userData.channels[0];
      // if (state.services.isMediaManager) {
      //     dispatch("setChannel", -1);
      // } else {
      //     dispatch("setChannel", defaultChannel.id);
      // }
    },
    /** Set current channel */
    setChannel({ state, commit }, channelId) {
      if (
        (channelId === -1 || channelId === -2) &&
        state.userData.channels.findIndex(
          item => item.id === -1 || item.id === -2
        ) === -1
      ) {
        state.userData.channels.unshift(
          {
            id: -1,
            channel_name: "All Streams",
            default_stream: "No Stream",
            stream_token: 1,
            stream_host: null,
            thumbs: null,
            httpStorage: null,
            mangomoloChannelId: null
          },
          {
            id: -2,
            channel_name: "Without Streams",
            default_stream: null,
            stream_token: 1,
            stream_host: null,
            thumbs: null,
            httpStorage: null,
            mangomoloChannelId: null
          }
        );
      }

      const targetChannel = state.userData.channels.find(
        ({ id }) => channelId == id
      );

      if (!targetChannel) {
        throw new Error(`Channel with ID <${channelId}> doesn't exist`);
      }
      commit("setChannelState", targetChannel);
    }
  },
  modules: {
    ndvrPlayer,
    timeline,
    thumbnails,
    socials,
    admin,
    timezone,
    modals
  }
});
